import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { Edit, ServerCrash } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { api, ApiError } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'

type EditPasswordProps = {
  applicationId: string
  clientKey: string
  currentPassword: string
  newPassword: string
}

async function editPassword(data: EditPasswordProps) {
  return api.backend.post('/application-auth/edit-secret-data', data)
}

export function useEditApplicationAuthPassword() {
  const { t } = useTranslation('sso')

  const { mutateAsync, isPending } = useMutation<
    unknown,
    AxiosError<ApiError>,
    EditPasswordProps
  >({
    mutationKey: ['/application-auth/edit-secret-data'],
    mutationFn: editPassword,
    onSuccess: () => {
      toast(Toast, {
        data: {
          type: 'success',
          icon: Edit,
          title: t(
            'applications.drawer_manage_auth.toast_edit_credentials_success.title',
          ),
        },
      })
      queryClient.invalidateQueries({ queryKey: ['/application-auth/findOne'] })
    },
    onError: (error) => {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: ServerCrash,
          title: t(
            'applications.drawer_manage_auth.toast_edit_credentials_error.title',
          ),
          description:
            error?.response?.data?.friendlyMessage ||
            t(
              'applications.drawer_manage_auth.toast_edit_credentials_error.description',
            ),
        },
      })
    },
  })

  const handleEditPassword = async (data: EditPasswordProps) => {
    return mutateAsync(data)
  }

  return {
    handleEditPassword,
    isEditingPassword: isPending,
  }
}
